import { useQuery, useMutation } from "@apollo/client/react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useCallback } from "react";
import { toast } from "sonner";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import {
  GetRecurringChargesDocument,
  GetRecurringChargeDocument,
  CreateRecurringChargeDocument,
  UpdateRecurringChargeDocument,
  DeleteRecurringChargeDocument,
  type GetRecurringChargesQuery,
  type GetRecurringChargeQuery,
  type CreateRecurringChargeMutation,
  type UpdateRecurringChargeMutation,
  type DeleteRecurringChargeMutation,
  type Recurring_ChargesInsertInput,
  type Recurring_ChargesUpdateInput,
} from "@/src/gql/graphql";
import type {
  RecurringCharge,
  RecurringChargeFilters,
  ChargeCardOnFileResponse,
} from "@/lib/types/recurring-charges";
import { mapRecurringChargeEdges, mapRecurringChargeNode } from "@/lib/types/quickbooks/recurring-charges";

export function useRecurringCharges(filters?: RecurringChargeFilters) {
  const { data, loading, error, refetch } = useQuery<GetRecurringChargesQuery>(
    GetRecurringChargesDocument,
    {
      variables: {
        first: 100,
        filter: {
          ...(filters?.customerId && { customer_id: { eq: filters.customerId } }),
          ...(filters?.scheduleType && { schedule_type: { eq: filters.scheduleType } }),
          ...(filters?.nextChargeDateFrom && filters?.nextChargeDateTo && {
            next_charge_date: {
              gte: filters.nextChargeDateFrom,
              lte: filters.nextChargeDateTo,
            },
          }),
        },
      },
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    }
  );

  const charges: RecurringCharge[] = useMemo(
    () => mapRecurringChargeEdges(data?.recurring_chargesCollection?.edges),
    [data]
  );

  return {
    data: charges,
    loading,
    error,
    refetch,
    totalCount: data?.recurring_chargesCollection?.totalCount ?? charges.length,
    pageInfo: data?.recurring_chargesCollection?.pageInfo,
  };
}

export function useRecurringCharge(id: string) {
  const { data, loading, error, refetch } = useQuery<GetRecurringChargeQuery>(
    GetRecurringChargeDocument,
    {
      variables: { id },
      errorPolicy: "all",
      skip: !id,
    }
  );

  const charge: RecurringCharge | null = useMemo(() => {
    const node = data?.recurring_chargesCollection?.edges?.[0]?.node;
    if (!node) {
      return null;
    }
    return mapRecurringChargeNode(node);
  }, [data]);

  return {
    data: charge,
    loading,
    error,
    refetch,
  };
}

export function useCreateRecurringCharge() {
  const queryClient = useQueryClient();

  const [mutate, { loading, error }] = useMutation<CreateRecurringChargeMutation>(
    CreateRecurringChargeDocument,
    {
      onCompleted: () => {
        void queryClient.invalidateQueries({ queryKey: ["recurringCharges"] });
        toast.success("Recurring charge created successfully");
      },
      onError: (mutationError: Error) => {
        toast.error(mutationError.message || "Failed to create recurring charge");
      },
    }
  );

  const mutateAsync = useCallback(
    async (data: Recurring_ChargesInsertInput): Promise<RecurringCharge> => {
      const result = await mutate({ variables: { input: data } });
      const record = result.data?.insertIntorecurring_chargesCollection?.records?.[0];
      if (!record) {
        throw new Error("Failed to create recurring charge");
      }
      return mapRecurringChargeNode(record);
    },
    [mutate]
  );

  return {
    mutateAsync,
    isPending: loading,
    error,
  };
}

export function useUpdateRecurringCharge() {
  const queryClient = useQueryClient();

  const [mutate, { loading, error }] = useMutation<UpdateRecurringChargeMutation>(
    UpdateRecurringChargeDocument,
    {
      onCompleted: () => {
        void queryClient.invalidateQueries({ queryKey: ["recurringCharges"] });
        toast.success("Recurring charge updated successfully");
      },
      onError: (mutationError: Error) => {
        toast.error(mutationError.message || "Failed to update recurring charge");
      },
    }
  );

  const mutateAsync = useCallback(
    async (data: Recurring_ChargesUpdateInput & { id: string }): Promise<RecurringCharge> => {
      const { id, ...input } = data;
      const result = await mutate({ variables: { id, input } });
      const record = result.data?.updaterecurring_chargesCollection?.records?.[0];
      if (!record) {
        throw new Error("Failed to update recurring charge");
      }
      return mapRecurringChargeNode(record);
    },
    [mutate]
  );

  return {
    mutateAsync,
    isPending: loading,
    error,
  };
}

export function useDeleteRecurringCharge() {
  const queryClient = useQueryClient();

  const [mutate, { loading, error }] = useMutation<DeleteRecurringChargeMutation>(
    DeleteRecurringChargeDocument,
    {
      onCompleted: () => {
        void queryClient.invalidateQueries({ queryKey: ["recurringCharges"] });
        toast.success("Recurring charge deleted successfully");
      },
      onError: (mutationError: Error) => {
        toast.error(mutationError.message || "Failed to delete recurring charge");
      },
    }
  );

  const mutateAsync = useCallback(
    async (id: string): Promise<string> => {
      await mutate({ variables: { id } });
      return id;
    },
    [mutate]
  );

  return {
    mutateAsync,
    isPending: loading,
    error,
  };
}

export function useChargeCardOnFile() {
  const queryClient = useQueryClient();
  const { session } = useUniversalAuth();
  const token = session?.access_token;

  const chargeCardOnFile = useCallback(
    async (chargeId: string): Promise<ChargeCardOnFileResponse> => {
      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch("/api/recurring-charges/charge", {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({ chargeId }),
        });

        if (!response.ok) {
          const errorBody: unknown = await response.json();
          const message =
            typeof errorBody === "object" &&
            errorBody !== null &&
            "error" in errorBody &&
            typeof errorBody.error === "string"
              ? errorBody.error
              : "Failed to charge card on file";
          throw new Error(message);
        }

        const result = (await response.json()) as ChargeCardOnFileResponse;

        if (result.success) {
          toast.success("Card charged successfully");
          void queryClient.invalidateQueries({ queryKey: ["recurringCharges"] });
        }

        return result;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to charge card";
        toast.error(message);
        return {
          success: false,
          error: message,
        };
      }
    },
    [queryClient, token]
  );

  return {
    chargeCardOnFile,
  };
}
